import { datadogRum } from '@datadog/browser-rum';

type BrandTheme = {
  brand: Record<number, string>;
  header: {
    background: string;
    'progress-circles': string;
  };
  cta: {
    background: string;
    font: string;
  };
  footer: {
    background: string;
  };
  fontFamily: string;
}

let cachedTheme: BrandTheme | null = null;

export const _resetCachedTheme = () => {
  cachedTheme = null;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const _setCachedTheme = (value: any) => {
  cachedTheme = value;
}

const defaultOptions = {
  defaultBrandRGBAColour: '78,125,210,1',
  defaultHeaderBackgroundHEXColor: '#ffffff',
  defaultHeaderProgressCirclesHEXColour: '#4e7dd2',
  defaultCTABackgroundHEXColour: '#4e7dd2',
  defaultCTAFontHEXColour: '#ffffff',
  defaultFooterBackgroundHEXColour: '#4e7dd2',
  defaultFontFamily: 'DM Sans',
  defaultOpacityVariations: ['0.5', '0.24', '0.16', '0.08'],
}

export function getTheme(): BrandTheme {
  if (process.env.NUXT_ENV_IS_NEXT_ENVIRONMENT === 'true') {
    throw new Error('getTheme() should not be used outside of Amplify');
  }

  if (cachedTheme !== null) return cachedTheme;

  const partnerConfig = process.env.NUXT_ENV_PARTNER_CONFIG_JSON ? JSON.parse(process.env.NUXT_ENV_PARTNER_CONFIG_JSON) : undefined;

  if (partnerConfig === undefined) {
    const error = new Error('Partner configuration not found');
    datadogRum.addError(error);
    throw error
  }

  let brandColor: string = defaultOptions.defaultBrandRGBAColour;

  if (partnerConfig.colours && typeof partnerConfig.colours === 'object' && 'brand' in partnerConfig.colours && typeof partnerConfig.colours.brand === 'string') {
    brandColor = partnerConfig.colours.brand.replace(/\s/g, '');
  }

  const brandColorRegex = /^(\d{1,3},){3}(\d.\d{1,2}|\d)$/;

  if (brandColorRegex.test(brandColor) === false) {
    throw new Error(`The brand color should be in rgba format, e.g. "${defaultOptions.defaultBrandRGBAColour}". Check the partner setup on Admin Extranet.`)
  }

  let headerBackgroundColor = defaultOptions.defaultHeaderBackgroundHEXColor;
  let headerProgressCirclesColour = defaultOptions.defaultHeaderProgressCirclesHEXColour;
  let ctaBackgroundColour = defaultOptions.defaultCTABackgroundHEXColour;
  let ctaFontColour = defaultOptions.defaultCTAFontHEXColour;
  let footerBackgroundColour = defaultOptions.defaultFooterBackgroundHEXColour;

  if (
    partnerConfig.colours && typeof partnerConfig.colours === 'object'
    && 'header' in partnerConfig.colours && typeof partnerConfig.colours.header === 'object' && partnerConfig.colours.header !== null
    && 'background' in partnerConfig.colours.header && typeof partnerConfig.colours.header.background === 'string'
  ) {
    headerBackgroundColor = rgbaToHex(partnerConfig.colours.header.background)
  }

  if (
    partnerConfig.colours && typeof partnerConfig.colours === 'object'
    && 'header' in partnerConfig.colours && typeof partnerConfig.colours.header === 'object' && partnerConfig.colours.header !== null
    && 'progress_circles' in partnerConfig.colours.header && typeof partnerConfig.colours.header.progress_circles === 'string'
  ) {
    headerProgressCirclesColour = rgbaToHex(partnerConfig.colours.header.progress_circles)
  }

  if (
    partnerConfig.colours && typeof partnerConfig.colours === 'object'
    && 'cta' in partnerConfig.colours && typeof partnerConfig.colours.cta === 'object' && partnerConfig.colours.cta !== null
    && 'action_button_background' in partnerConfig.colours.cta && typeof partnerConfig.colours.cta.action_button_background === 'string'
  ) {
    ctaBackgroundColour = rgbaToHex(partnerConfig.colours.cta.action_button_background)
  }

  if (
    partnerConfig.colours && typeof partnerConfig.colours === 'object'
    && 'cta' in partnerConfig.colours && typeof partnerConfig.colours.cta === 'object' && partnerConfig.colours.cta !== null
    && 'action_button_font_colour' in partnerConfig.colours.cta && typeof partnerConfig.colours.cta.action_button_font_colour === 'string'
  ) {
    ctaFontColour = rgbaToHex(partnerConfig.colours.cta.action_button_font_colour)
  }

  if (
    partnerConfig.colours && typeof partnerConfig.colours === 'object'
    && 'footer' in partnerConfig.colours && typeof partnerConfig.colours.footer === 'object' && partnerConfig.colours.footer !== null
    && 'background' in partnerConfig.colours.footer && typeof partnerConfig.colours.footer.background === 'string'
  ) {
    footerBackgroundColour = rgbaToHex(partnerConfig.colours.footer.background)
  }

  const baseColorHex = rgbaToHex(brandColor, true);
  const theme: Partial<BrandTheme> = {}
  const opacityVariations = defaultOptions.defaultOpacityVariations.map((opacity: string) => parseFloat(opacity));

  const addAlpha = (color: string, opacity: number) => {
    const generatedOpacity = Math.round(Math.min(Math.max(opacity || 1, 0), 1) * 255);
    return color + generatedOpacity.toString(16).toUpperCase();
  }

  const reduceOpacityVariations = (accumulator: Record<number, string>, variation: number): Record<number, string> => {
    const key = variation * 100;
    return { ...accumulator, [key]: addAlpha(baseColorHex, variation) }
  }

  theme.brand = opacityVariations.reduce(reduceOpacityVariations, { 100: baseColorHex })
  theme.header = {
    background: headerBackgroundColor,
    'progress-circles': headerProgressCirclesColour,
  }
  theme.cta = {
    background: ctaBackgroundColour,
    font: ctaFontColour,
  }
  theme.footer = {
    background: footerBackgroundColour,
  }
  theme.fontFamily = defaultOptions.defaultFontFamily;

  cachedTheme = theme as BrandTheme;
  return theme as BrandTheme;
}

/**
 * Converts an RGBA colour to a HEX colour string
 *
 * @param orig
 * @param skipAlpha
 * @returns
 */
export function rgbaToHex(orig: string, skipAlpha: boolean = false): string {
  const rgba = orig.split(',').map((val) => val.trim());

  let hex = `${
    ((rgba[0] as unknown as number) | (1 << 8)).toString(16).slice(1)
  }${
    ((rgba[1] as unknown as number) | (1 << 8)).toString(16).slice(1)
  }${
    ((rgba[2] as unknown as number) | (1 << 8)).toString(16).slice(1)
  }`;

  if (skipAlpha) {
    return `#${hex}`;
  }

  const alpha = ((rgba?.[3]) || '').trim();

  let a;

  if (alpha !== '') a = alpha;
  else a = 0o1;

  a = (((a as unknown as number) * 255) | (1 << 8)).toString(16).slice(1);
  hex = `#${hex + a}`;

  return hex.toUpperCase();
}
